import React, { memo, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import CreateIcon from "@mui/icons-material/Create";
import { palette } from "../../theme/palette";
import { BinIcon } from "../Icons";
import { Text } from "../Base";
import { View } from "../View";
import { ArtificialPostcodeLabel } from "./ArtificialPostcodeLabel";
import { POSTCODE } from "../../consts/layers";
import { searchHandler } from "../../reducers/ownApp/thunkActions";
import { setArtificialPostcodeEdit } from "../../reducers/depot/thunkActions";
import { setSelectedArtificialPostcode } from "../../reducers/depot";
import { RemoveArtificialPostcodeModal } from "../Modals/RemoveArtificialPostcode/RemoveArtificialPostcodeModal";
import { addNotification } from "../../reducers/ownApp";
import { EDIT_ARTIFICIAL_POSTCODE_ACTIVE_WARNING } from "../../consts/strings";

const useStyles = makeStyles(() => ({
  section: {
    display: "flex",
    flexDirection: "row",
    background: palette.typography.white.neutral,
    padding: "0.625rem",
    paddingBottom: "0.5rem",
    cursor: "default",
  },
  highlighted: {
    background: palette.typography.white.disabled,
  },
  editIconBase: {
    backgroundColor: (props) =>
      props.editing
        ? palette.orange
        : props.notInWave
        ? "#30303044"
        : "#303030",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    color: palette.typography.white.solid,
    width: "13px",
    height: "13px",
    cursor: "pointer",
  },
  binIcon: {
    marginTop: "10px",
    fill: palette.typography.white.solid,
    height: "20px",
    width: "20px",
    cursor: "pointer",
  },
  left: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
  },
  leftButtons: {
    width: "2.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginRight: "auto",
  },
  right: {
    flex: 1,
    display: "flex",
    marginTop: "0.75rem",
    marginLeft: "0.5rem",
    color: "white",
    lineHeight: "1.25rem",
    gap: "0.25rem",
  },
  cordsLabels: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  cordsContainer: {
    marginTop: "0.1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
  },
  cords: {
    fontSize: "0.875rem",
  },
}));

const ArtificialPostcodeCard = ({
  editMode,
  inEdit,
  selected,
  artificialPostcode,
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const dispatch = useDispatch();
  const styles = useStyles({
    editing: inEdit,
  });

  const handleClick = (e) => {
    e.preventDefault();
    dispatch(
      searchHandler({
        item: {
          type: artificialPostcode.level,
          value: artificialPostcode.postcode,
        },
        type: POSTCODE,
      })
    );
    dispatch(setSelectedArtificialPostcode({ artificialPostcode }));
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (!editMode) {
      dispatch(setSelectedArtificialPostcode());
      dispatch(setArtificialPostcodeEdit({ artificialPostcode }));
    } else if (!inEdit)
      dispatch(
        addNotification({
          message: EDIT_ARTIFICIAL_POSTCODE_ACTIVE_WARNING(
            artificialPostcode.postcode
          ),
          options: { variant: "info" },
        })
      );
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (!editMode || inEdit) setShowRemoveModal(true);
    else
      dispatch(
        addNotification({
          message: EDIT_ARTIFICIAL_POSTCODE_ACTIVE_WARNING(
            artificialPostcode.postcode
          ),
          options: { variant: "info" },
        })
      );
  };

  return (
    <View>
      <View
        className={clsx(
          styles.section,
          (inEdit || selected) && styles.highlighted
        )}
        onClick={handleClick}
      >
        <View className={styles.left}>
          <ArtificialPostcodeLabel artificialPostcode={artificialPostcode} />
          <View className={styles.leftButtons}>
            <View className={styles.editIconBase} onClick={handleEdit}>
              <CreateIcon fontSize={"small"} className={styles.editIcon} />
            </View>
            <BinIcon className={styles.binIcon} onClick={handleClear} />
          </View>
        </View>
        <View className={styles.right}>
          <View className={styles.cordsLabels}>
            <Text>Latitude:</Text>
            <Text>Longitude:</Text>
          </View>
          <View className={styles.cordsContainer}>
            <Text className={styles.cords}>
              {artificialPostcode?.lat ?? "???"}
            </Text>
            <Text className={styles.cords}>
              {artificialPostcode?.lng ?? "???"}
            </Text>
          </View>
        </View>
      </View>
      {showRemoveModal && (
        <RemoveArtificialPostcodeModal
          artificialPostcode={artificialPostcode}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </View>
  );
};

export default memo(ArtificialPostcodeCard);
